<template>
  <div class="home-layout">
    <div class="home-layout-toolbar">
      <EventMonthCalendar
        ref="calendarRef"
        :events="calendarEvents"
        :month="calendarMonth"
        @select-event="openEvent"
        @update:month="onMonthChange"
      />
    </div>

    <div v-if="auth.isAuthenticated" class="home-dashboard-mobile">
      <div class="dashboard-tab-bar" role="tablist" aria-label="Разделы событий">
        <button
          v-for="tab in dashboardTabs"
          :key="tab.id"
          type="button"
          class="dashboard-tab"
          :class="{ 'dashboard-tab--active': activeTab === tab.id }"
          role="tab"
          :aria-selected="activeTab === tab.id"
          @click="activeTab = tab.id"
        >
          {{ tab.label }}
        </button>
      </div>

      <section class="home-dashboard-panel panel">
        <div v-if="activeTab === 'personal'" class="home-dashboard-tab">
          <div class="column-header column-header--tabs">
            <div class="column-header-tabs-row">
              <div class="dashboard-tab-bar" role="tablist" aria-label="Мои и избранное">
                <button
                  v-for="tab in personalSubTabs"
                  :key="tab.id"
                  type="button"
                  class="dashboard-tab"
                  :class="{ 'dashboard-tab--active': personalSubTab === tab.id }"
                  role="tab"
                  :aria-selected="personalSubTab === tab.id"
                  @click="personalSubTab = tab.id"
                >
                  {{ tab.label }}
                </button>
              </div>
              <AddItemLink
                v-if="personalSubTab === 'my'"
                :to="{ name: 'event-new' }"
                label="Добавить событие"
              />
            </div>
          </div>
          <div v-if="personalSubTab === 'my'" class="column-body">
            <p v-if="myLoading" class="empty-state">Загрузка...</p>
            <EventList
              v-else
              :events="myEvents"
              empty-text="Пока нет сохранённых событий."
              @select="openEvent"
            />
          </div>
          <ListPager
            v-if="personalSubTab === 'my'"
            :page="myPage"
            :total-pages="myPagination?.total_pages || 0"
            :total="myPagination?.total || 0"
            :disabled="myLoading"
            @update:page="myPage = $event"
          />
          <div v-if="personalSubTab === 'favorites'" class="column-body">
            <p v-if="favoritesLoading" class="empty-state">Загрузка...</p>
            <template v-else>
              <EventList
                title="Избранные"
                :events="favoriteEvents"
                empty-text="Пока нет избранных событий."
                @select="openEvent"
              />
              <ListPager
                :page="favoriteEventsPage"
                :total-pages="favoriteEventsPagination?.total_pages || 0"
                :total="favoriteEventsPagination?.total || 0"
                :disabled="favoritesLoading"
                @update:page="favoriteEventsPage = $event"
              />
              <EventList
                title="Подписки"
                :events="subscribedEvents"
                empty-text="Пока нет подписок на события."
                @select="openEvent"
              />
              <ListPager
                :page="subscribedEventsPage"
                :total-pages="subscribedEventsPagination?.total_pages || 0"
                :total="subscribedEventsPagination?.total || 0"
                :disabled="favoritesLoading"
                @update:page="subscribedEventsPage = $event"
              />
            </template>
          </div>
        </div>

        <div v-else-if="activeTab === 'public'" class="home-dashboard-tab">
          <div class="column-header">
            <ColumnFilter
              v-model="publicEventsSort"
              :options="publicEventsFilterOptions"
              :loading="publicEventsLoading"
              @update:model-value="onPublicSortChange"
            />
          </div>
          <div class="column-body">
            <p v-if="publicEventsLoading" class="empty-state">Загрузка...</p>
            <EventList
              v-else
              :events="publicEvents"
              empty-text="Пока нет публичных событий."
              @select="openEvent"
            />
          </div>
          <ListPager
            :page="publicPage"
            :total-pages="publicPagination?.total_pages || 0"
            :total="publicPagination?.total || 0"
            :disabled="publicEventsLoading"
            @update:page="publicPage = $event"
          />
        </div>

        <div v-else class="home-dashboard-tab">
          <h2>Последние комментарии</h2>
          <div class="column-body">
            <RecentCommentFeed
              kind="event"
              :comments="recentComments"
              :loading="recentCommentsLoading"
            />
            <LoadMoreButton
              :has-more="hasMorePages(recentCommentsPagination)"
              :loading="recentCommentsLoadingMore"
              :disabled="recentCommentsLoading"
              @click="loadMoreRecentComments"
            />
          </div>
        </div>
      </section>
    </div>

    <div v-if="auth.isAuthenticated" class="home-columns home-columns--desktop">
      <section class="dashboard-column panel">
        <div class="column-header column-header--tabs">
          <div class="column-header-tabs-row">
            <div class="dashboard-tab-bar" role="tablist" aria-label="Мои и избранное">
              <button
                v-for="tab in personalSubTabs"
                :key="tab.id"
                type="button"
                class="dashboard-tab"
                :class="{ 'dashboard-tab--active': personalSubTab === tab.id }"
                role="tab"
                :aria-selected="personalSubTab === tab.id"
                @click="personalSubTab = tab.id"
              >
                {{ tab.label }}
              </button>
            </div>
            <AddItemLink
              v-if="personalSubTab === 'my'"
              :to="{ name: 'event-new' }"
              label="Добавить событие"
            />
          </div>
        </div>
        <div v-if="personalSubTab === 'my'" class="column-body">
          <p v-if="myLoading" class="empty-state">Загрузка...</p>
          <EventList
            v-else
            :events="myEvents"
            empty-text="Пока нет сохранённых событий."
            @select="openEvent"
          />
        </div>
        <ListPager
          v-if="personalSubTab === 'my'"
          :page="myPage"
          :total-pages="myPagination?.total_pages || 0"
          :total="myPagination?.total || 0"
          :disabled="myLoading"
          @update:page="myPage = $event"
        />
        <div v-else class="column-body">
          <p v-if="favoritesLoading" class="empty-state">Загрузка...</p>
          <template v-else>
            <EventList
              title="Избранные"
              :events="favoriteEvents"
              empty-text="Пока нет избранных событий."
              @select="openEvent"
            />
            <ListPager
              :page="favoriteEventsPage"
              :total-pages="favoriteEventsPagination?.total_pages || 0"
              :total="favoriteEventsPagination?.total || 0"
              :disabled="favoritesLoading"
              @update:page="favoriteEventsPage = $event"
            />
            <EventList
              title="Подписки"
              :events="subscribedEvents"
              empty-text="Пока нет подписок на события."
              @select="openEvent"
            />
            <ListPager
              :page="subscribedEventsPage"
              :total-pages="subscribedEventsPagination?.total_pages || 0"
              :total="subscribedEventsPagination?.total || 0"
              :disabled="favoritesLoading"
              @update:page="subscribedEventsPage = $event"
            />
          </template>
        </div>
      </section>

      <section class="dashboard-column panel">
        <div class="column-header">
          <ColumnFilter
            v-model="publicEventsSort"
            :options="publicEventsFilterOptions"
            :loading="publicEventsLoading"
            @update:model-value="onPublicSortChange"
          />
        </div>
        <div class="column-body">
          <p v-if="publicEventsLoading" class="empty-state">Загрузка...</p>
          <EventList
            v-else
            :events="publicEvents"
            empty-text="Пока нет публичных событий."
            @select="openEvent"
          />
        </div>
        <ListPager
          :page="publicPage"
          :total-pages="publicPagination?.total_pages || 0"
          :total="publicPagination?.total || 0"
          :disabled="publicEventsLoading"
          @update:page="publicPage = $event"
        />
      </section>

      <section class="dashboard-column panel">
        <h2>Последние комментарии</h2>
        <div class="column-body">
          <RecentCommentFeed
            kind="event"
            :comments="recentComments"
            :loading="recentCommentsLoading"
          />
          <LoadMoreButton
            :has-more="hasMorePages(recentCommentsPagination)"
            :loading="recentCommentsLoadingMore"
            :disabled="recentCommentsLoading"
            @click="loadMoreRecentComments"
          />
        </div>
      </section>
    </div>

    <div v-else class="guest-home">
      <div class="guest-home-mobile home-dashboard-mobile">
        <div class="dashboard-tab-bar" role="tablist" aria-label="Разделы событий">
          <button
            v-for="tab in guestTabs"
            :key="tab.id"
            type="button"
            class="dashboard-tab"
            :class="{ 'dashboard-tab--active': guestActiveTab === tab.id }"
            role="tab"
            :aria-selected="guestActiveTab === tab.id"
            @click="guestActiveTab = tab.id"
          >
            {{ tab.label }}
          </button>
        </div>

        <section class="home-dashboard-panel panel">
          <div v-if="guestActiveTab === 'public'" class="home-dashboard-tab">
            <div class="column-header">
              <h1>Публичные события</h1>
              <ColumnFilter
                v-model="publicEventsSort"
                :options="publicEventsFilterOptions"
                :loading="publicEventsLoading"
                @update:model-value="onPublicSortChange"
              />
            </div>
            <div class="column-body">
              <p v-if="publicEventsLoading" class="empty-state">Загрузка...</p>
              <EventList
                v-else
                :events="publicEvents"
                empty-text="Пока нет публичных событий."
                @select="openEvent"
              />
            </div>
            <ListPager
              :page="publicPage"
              :total-pages="publicPagination?.total_pages || 0"
              :total="publicPagination?.total || 0"
              :disabled="publicEventsLoading"
              @update:page="publicPage = $event"
            />
          </div>

          <div v-else class="home-dashboard-tab">
            <h2 class="guest-column-title">Последние комментарии</h2>
            <div class="column-body">
              <RecentCommentFeed
                kind="event"
                :comments="recentComments"
                :loading="recentCommentsLoading"
              />
              <LoadMoreButton
                :has-more="hasMorePages(recentCommentsPagination)"
                :loading="recentCommentsLoadingMore"
                :disabled="recentCommentsLoading"
                @click="loadMoreRecentComments"
              />
            </div>
          </div>
        </section>
      </div>

      <div class="guest-home-columns guest-home-columns--desktop">
        <section class="panel guest-home-column">
          <div class="column-header">
            <h1>Публичные события</h1>
            <ColumnFilter
              v-model="publicEventsSort"
              :options="publicEventsFilterOptions"
              :loading="publicEventsLoading"
              @update:model-value="onPublicSortChange"
            />
          </div>
          <div class="column-body">
            <p v-if="publicEventsLoading" class="empty-state">Загрузка...</p>
            <EventList
              v-else
              :events="publicEvents"
              empty-text="Пока нет публичных событий."
              @select="openEvent"
            />
          </div>
          <ListPager
            :page="publicPage"
            :total-pages="publicPagination?.total_pages || 0"
            :total="publicPagination?.total || 0"
            :disabled="publicEventsLoading"
            @update:page="publicPage = $event"
          />
        </section>

        <section class="panel guest-home-column">
          <h2 class="guest-column-title">Последние комментарии</h2>
          <div class="column-body">
            <RecentCommentFeed
              kind="event"
              :comments="recentComments"
              :loading="recentCommentsLoading"
            />
            <LoadMoreButton
              :has-more="hasMorePages(recentCommentsPagination)"
              :loading="recentCommentsLoadingMore"
              :disabled="recentCommentsLoading"
              @click="loadMoreRecentComments"
            />
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import AddItemLink from '../components/AddItemLink.vue'
import ColumnFilter from '../components/ColumnFilter.vue'
import EventList from '../components/EventList.vue'
import EventMonthCalendar from '../components/EventMonthCalendar.vue'
import ListPager from '../components/ListPager.vue'
import LoadMoreButton from '../components/LoadMoreButton.vue'
import RecentCommentFeed from '../components/RecentCommentFeed.vue'
import { useMediaQuery } from '../composables/useMediaQuery'
import {
  fetchEvents,
  fetchFavoriteEvents,
  fetchPublicEventComments,
  fetchPublicEvents,
  fetchSubscribedEvents,
} from '../api/events'
import { useAuthStore } from '../stores/auth'
import { monthRange } from '../utils/eventFormat'
import { hasMorePages, parsePaginatedList } from '../utils/pagination'

const PAGE_SIZE = 10

const router = useRouter()
const auth = useAuthStore()
const isMobile = useMediaQuery('(max-width: 767px)')

const calendarEvents = ref([])
const myEvents = ref([])
const publicEvents = ref([])
const favoriteEvents = ref([])
const subscribedEvents = ref([])
const publicEventsSort = ref('upcoming')
const publicEventsLoading = ref(false)
const myLoading = ref(false)
const favoritesLoading = ref(false)
const recentComments = ref([])
const recentCommentsLoading = ref(false)
const recentCommentsLoadingMore = ref(false)
const activeTab = ref('personal')
const personalSubTab = ref('my')

const myPage = ref(1)
const publicPage = ref(1)
const favoriteEventsPage = ref(1)
const subscribedEventsPage = ref(1)
const recentCommentsPage = ref(1)
const myPagination = ref(null)
const publicPagination = ref(null)
const favoriteEventsPagination = ref(null)
const subscribedEventsPagination = ref(null)
const recentCommentsPagination = ref(null)

const dashboardTabs = [
  { id: 'personal', label: 'Личное' },
  { id: 'public', label: 'На сайте' },
  { id: 'comments', label: 'Комментарии' },
]

const personalSubTabs = [
  { id: 'my', label: 'Мои события' },
  { id: 'favorites', label: 'Избранное' },
]

const guestActiveTab = ref('public')

const guestTabs = [
  { id: 'public', label: 'На сайте' },
  { id: 'comments', label: 'Комментарии' },
]

const calendarMonth = ref(new Date())
const calendarRef = ref(null)

const publicEventsFilterOptions = [
  { value: 'upcoming', label: 'Ближайшие' },
  { value: 'new', label: 'Новые на сайте' },
  { value: 'most_commented', label: 'Самые обсуждаемые' },
  { value: 'most_liked', label: 'Самые популярные' },
]

function monthParams() {
  return monthRange(calendarMonth.value)
}

function mergeCalendarEvents(publicItems, myItems = []) {
  const merged = new Map()
  publicItems.forEach((event) => merged.set(event.id, event))
  myItems.forEach((event) => merged.set(event.id, event))
  calendarEvents.value = Array.from(merged.values())
}

function onMonthChange(month) {
  calendarMonth.value = month
  loadCalendarEvents()
}

function onPublicSortChange() {
  publicPage.value = 1
  loadPublicEvents()
}

async function loadCalendarEvents() {
  const { from, to } = monthParams()
  const requests = [fetchPublicEvents({ from, to, sort: 'upcoming' })]
  if (auth.isAuthenticated) {
    requests.push(fetchEvents({ from, to }))
  }

  const responses = await Promise.all(requests)
  const publicItems = Array.isArray(responses[0].data) ? responses[0].data : []
  const myItems = responses[1] && Array.isArray(responses[1].data) ? responses[1].data : []
  mergeCalendarEvents(publicItems, myItems)
}

async function loadMyEvents() {
  myLoading.value = true
  try {
    const response = await fetchEvents({
      page: myPage.value,
      perPage: PAGE_SIZE,
    })
    const { items, pagination } = parsePaginatedList(response, 'events')
    myEvents.value = items
    myPagination.value = pagination
  } finally {
    myLoading.value = false
  }
}

async function loadPublicEvents() {
  publicEventsLoading.value = true
  try {
    const response = await fetchPublicEvents({
      sort: publicEventsSort.value,
      page: publicPage.value,
      perPage: PAGE_SIZE,
    })
    const { items, pagination } = parsePaginatedList(response, 'events')
    publicEvents.value = items
    publicPagination.value = pagination
  } finally {
    publicEventsLoading.value = false
  }
}

async function loadFavorites() {
  favoritesLoading.value = true
  try {
    const [favoriteResponse, subscribedResponse] = await Promise.all([
      fetchFavoriteEvents({ page: favoriteEventsPage.value, perPage: PAGE_SIZE }),
      fetchSubscribedEvents({ page: subscribedEventsPage.value, perPage: PAGE_SIZE }),
    ])
    const favoriteParsed = parsePaginatedList(favoriteResponse, 'events')
    const subscribedParsed = parsePaginatedList(subscribedResponse, 'events')
    favoriteEvents.value = favoriteParsed.items
    favoriteEventsPagination.value = favoriteParsed.pagination
    subscribedEvents.value = subscribedParsed.items
    subscribedEventsPagination.value = subscribedParsed.pagination
  } finally {
    favoritesLoading.value = false
  }
}

async function loadPersonalMobile() {
  if (personalSubTab.value === 'my') {
    await loadMyEvents()
  } else {
    await loadFavorites()
  }
}

async function loadMobileTab(tab = activeTab.value) {
  if (tab === 'personal') {
    await loadPersonalMobile()
  } else if (tab === 'public') {
    await loadPublicEvents()
  } else {
    await loadRecentComments({ reset: true })
  }
}

async function loadRecentComments({ reset = true } = {}) {
  if (reset) {
    recentCommentsLoading.value = true
    recentCommentsPage.value = 1
  } else {
    recentCommentsLoadingMore.value = true
  }
  try {
    const response = await fetchPublicEventComments({
      page: recentCommentsPage.value,
      perPage: PAGE_SIZE,
    })
    const { items, pagination } = parsePaginatedList(response, 'comments')
    recentComments.value = reset ? items : [...recentComments.value, ...items]
    recentCommentsPagination.value = pagination
  } finally {
    recentCommentsLoading.value = false
    recentCommentsLoadingMore.value = false
  }
}

function loadMoreRecentComments() {
  recentCommentsPage.value += 1
  loadRecentComments({ reset: false })
}

async function loadDesktopEvents() {
  await Promise.all([
    loadMyEvents(),
    loadFavorites(),
    loadPublicEvents(),
    loadRecentComments(),
  ])
}

async function loadEvents() {
  await loadCalendarEvents()

  if (!auth.isAuthenticated) {
    await Promise.all([loadPublicEvents(), loadRecentComments({ reset: true })])
    return
  }

  if (isMobile.value) {
    await loadMobileTab()
  } else {
    await loadDesktopEvents()
  }
}

function openEvent(id) {
  router.push({ name: 'event', params: { id } })
}

watch(isMobile, () => {
  loadEvents()
})

watch(activeTab, (tab) => {
  if (!auth.isAuthenticated || !isMobile.value) return
  loadMobileTab(tab)
})

watch(personalSubTab, () => {
  if (!auth.isAuthenticated) return
  if (isMobile.value && activeTab.value !== 'personal') return
  loadPersonalMobile()
})

watch(myPage, () => {
  if (auth.isAuthenticated) {
    if (isMobile.value && (activeTab.value !== 'personal' || personalSubTab.value !== 'my')) return
    if (!isMobile.value && personalSubTab.value !== 'my') return
  }
  loadMyEvents()
})

watch(publicPage, () => {
  if (auth.isAuthenticated && isMobile.value && activeTab.value !== 'public') return
  if (!auth.isAuthenticated && isMobile.value && guestActiveTab.value !== 'public') return
  loadPublicEvents()
})

watch(favoriteEventsPage, () => {
  if (!auth.isAuthenticated) return
  if (isMobile.value && (activeTab.value !== 'personal' || personalSubTab.value !== 'favorites')) return
  if (!isMobile.value && personalSubTab.value !== 'favorites') return
  loadFavorites()
})

watch(subscribedEventsPage, () => {
  if (!auth.isAuthenticated) return
  if (isMobile.value && (activeTab.value !== 'personal' || personalSubTab.value !== 'favorites')) return
  if (!isMobile.value && personalSubTab.value !== 'favorites') return
  loadFavorites()
})

watch(guestActiveTab, (tab) => {
  if (auth.isAuthenticated || !isMobile.value) return
  if (tab === 'public') {
    loadPublicEvents()
  } else {
    loadRecentComments({ reset: true })
  }
})

onMounted(loadEvents)
</script>
